import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Star,
  StarOff,
  MailPlus,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

/**
 * EmailTemplatesManager — v1.4.1 (17/07/2026).
 *
 * Sezione delle Impostazioni Studio che permette di creare, modificare
 * ed eliminare più template email (es. Fattura standard, Sollecito,
 * Proforma, Nota di credito). Uno solo può essere impostato come
 * default: sarà quello preselezionato nel menu "Invia per email" delle
 * fatture.
 *
 * Backend:
 *   GET    /api/settings/email-templates
 *   POST   /api/settings/email-templates
 *   DELETE /api/settings/email-templates/{id}
 *
 * Variabili disponibili nei placeholder:
 *   {invoiceNumber}, {invoiceDate}, {clientName}, {totale}, {studioName}
 */
const PLACEHOLDER_HELP =
  "Variabili disponibili: {invoiceNumber}, {invoiceDate}, {clientName}, {totale}, {studioName}";

const emptyTemplate = { id: null, name: "", subject: "", body: "", isDefault: false };

export default function EmailTemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | template
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await api.getEmailTemplates();
      setTemplates(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("[EmailTemplates] load error", err);
      toast.error("Errore caricamento template email");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing({ ...emptyTemplate });
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing({ ...tpl });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing) return;
    const name = (editing.name || "").trim();
    const subject = (editing.subject || "").trim();
    const body = (editing.body || "").trim();
    if (!name) return toast.error("Il nome del template è obbligatorio");
    if (!subject) return toast.error("L'oggetto è obbligatorio");
    if (!body) return toast.error("Il corpo del template è obbligatorio");
    try {
      setSaving(true);
      const res = await api.saveEmailTemplate({
        id: editing.id || undefined,
        name,
        subject,
        body,
        isDefault: !!editing.isDefault,
      });
      setTemplates(res.templates || []);
      toast.success(editing.id ? "Template aggiornato" : "Template creato");
      closeDialog();
    } catch (err) {
      toast.error(err?.message || "Errore salvataggio template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (templates.length <= 1) {
      toast.error("Non puoi eliminare l'unico template esistente");
      return;
    }
    if (!window.confirm(`Eliminare il template "${tpl.name}"?`)) return;
    try {
      setDeletingId(tpl.id);
      const res = await api.deleteEmailTemplate(tpl.id);
      setTemplates(res.templates || []);
      toast.success("Template eliminato");
    } catch (err) {
      toast.error(err?.message || "Errore eliminazione template");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleDefault = async (tpl) => {
    if (tpl.isDefault) return; // già default
    try {
      const res = await api.saveEmailTemplate({
        id: tpl.id,
        name: tpl.name,
        subject: tpl.subject,
        body: tpl.body,
        isDefault: true,
      });
      setTemplates(res.templates || []);
      toast.success(`"${tpl.name}" è ora il template predefinito`);
    } catch (err) {
      toast.error(err?.message || "Errore aggiornamento default");
    }
  };

  return (
    <Card className="shadow-md border-orange-200" data-testid="email-templates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-orange-600" />
          Template Email Fatture
        </CardTitle>
        <CardDescription>
          Crea più template (es. Fattura standard, Sollecito, Proforma, Nota di credito) e scegli
          quello da usare al momento dell&apos;invio. {PLACEHOLDER_HELP}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            Caricamento template…
          </div>
        ) : (
          <>
            {templates.length === 0 && (
              <div className="text-sm text-slate-500 italic">Nessun template configurato.</div>
            )}
            <ul className="space-y-2" data-testid="email-templates-list">
              {templates.map((tpl) => (
                <li
                  key={tpl.id}
                  className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-orange-300 transition-colors"
                  data-testid={`email-template-item-${tpl.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{tpl.name}</span>
                      {tpl.isDefault && (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-200 gap-1">
                          <Star className="h-3 w-3" /> Predefinito
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 truncate" title={tpl.subject}>
                      <span className="font-medium">Oggetto:</span> {tpl.subject}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2" title={tpl.body}>
                      {tpl.body}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!tpl.isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleDefault(tpl)}
                        title="Imposta come predefinito"
                        data-testid={`email-template-set-default-${tpl.id}`}
                      >
                        <StarOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(tpl)}
                      title="Modifica"
                      data-testid={`email-template-edit-${tpl.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(tpl)}
                      disabled={deletingId === tpl.id || templates.length <= 1}
                      title={templates.length <= 1 ? "Impossibile eliminare l'unico template" : "Elimina"}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`email-template-delete-${tpl.id}`}
                    >
                      {deletingId === tpl.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={openNew}
              data-testid="email-template-new-button"
            >
              <Plus className="mr-2 h-4 w-4" /> Nuovo Template
            </Button>
          </>
        )}

        <Dialog open={dialogOpen} onOpenChange={(v) => (v ? setDialogOpen(true) : closeDialog())}>
          <DialogContent className="max-w-2xl" data-testid="email-template-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MailPlus className="h-5 w-5 text-orange-600" />
                {editing?.id ? "Modifica Template" : "Nuovo Template"}
              </DialogTitle>
              <DialogDescription>{PLACEHOLDER_HELP}</DialogDescription>
            </DialogHeader>
            {editing && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="tpl-name">Nome Template *</Label>
                  <Input
                    id="tpl-name"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="es. Sollecito pagamento"
                    data-testid="email-template-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="tpl-subject">Oggetto *</Label>
                  <Input
                    id="tpl-subject"
                    value={editing.subject}
                    onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                    placeholder="Fattura n. {invoiceNumber} del {invoiceDate}"
                    data-testid="email-template-subject-input"
                  />
                </div>
                <div>
                  <Label htmlFor="tpl-body">Corpo Email *</Label>
                  <Textarea
                    id="tpl-body"
                    value={editing.body}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    rows={8}
                    placeholder="Gentile {clientName},..."
                    data-testid="email-template-body-input"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none text-sm">
                  <input
                    type="checkbox"
                    checked={!!editing.isDefault}
                    onChange={(e) => setEditing({ ...editing, isDefault: e.target.checked })}
                    data-testid="email-template-default-checkbox"
                  />
                  <span>Imposta come template predefinito per invio fatture</span>
                </label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={closeDialog} disabled={saving}>
                Annulla
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="email-template-save-button">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {editing?.id ? "Aggiorna" : "Crea Template"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
